import { z } from 'zod/v3';

import { SortDirection } from '@creditornot/cb-data-table';
import { CountryCode, SortingDirection } from 'common/api-types/common';
import {
  PORecommendation,
  PORecommendationsRequestParams,
} from 'common/api-types/ordering-automation';
import {
  PurchaseOrder,
  PurchaseOrdersRequestParams,
  StorageTemperatureType,
} from 'common/api-types/vendor';

import { ItemsSortableColumn, RecommendationItemsSortableColumn } from './types';
import { ApprovalType } from './views/POs/RecommendationsFilter/RecommendationsFilter';

const purchaseOrdersModals = z.object({
  createModalOpen: z.boolean().optional(),
  waybillEditModalOpen: z.boolean().optional(),
  waybillsBulkUploadModalOpen: z.boolean().optional(),
  poId: z.string().optional(),
  downloadForecastModalOpen: z.boolean().optional(),
});

const purchaseOrdersRequestParams = z.object({
  venueCodes: z.custom<PurchaseOrdersRequestParams['venueCodes']>().optional(),
  vendorCodes: z.custom<PurchaseOrdersRequestParams['vendorCodes']>().optional(),
  publicId: z.string().optional(),
  earliestFrom: z.string().optional(),
  latestTo: z.string().optional(),
  status: z.custom<PurchaseOrder['status'] | 'all' | 'recommended'>().optional(),
  countryCodes: z.custom<PurchaseOrdersRequestParams['countryCodes']>().optional(),
  sortBy: z.custom<PurchaseOrdersRequestParams['sortBy']>().optional(),
  sortingType: z.custom<PurchaseOrdersRequestParams['sortingType']>().optional(),
  confirmedBy: z.custom<PurchaseOrdersRequestParams['confirmedBy']>().optional(),
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  q: z.coerce.string().optional(),
});

const recommendationsRequestParams = z.object({
  recommendationSortBy: z.custom<PORecommendationsRequestParams['sortBy']>().optional(),
  recommendationSortDirection: z.custom<SortingDirection>().optional(),
  recommendationStatuses: z.custom<PORecommendation['status'][]>().optional(),
  approvalType: z.custom<ApprovalType>().optional(),
});

export const purchaseOrdersRouteSearchSchema = z.object({
  ...purchaseOrdersModals.shape,
  ...purchaseOrdersRequestParams.shape,
  ...recommendationsRequestParams.shape,
});

const poDetailsRequestParams = z.object({
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  sort: z.custom<ItemsSortableColumn>().optional(),
  sortOrder: z.custom<SortDirection>().optional(),
  q: z.coerce.string().optional(),
});

const poDetailsModals = z.object({
  history: z.boolean().optional(),
  waybillEditOpen: z.boolean().optional(),
});

export const poDetailsRouteSearchSchema = z.object({
  ...poDetailsRequestParams.shape,
  ...poDetailsModals.shape,
  from: z.string().optional(),
});

export const recommendationRouteSearchSchema = z.object({
  itemsQ: z.string().optional(),
  sort: z.custom<RecommendationItemsSortableColumn>().optional(),
  sortOrder: z.custom<SortDirection>().optional(),
  hasSuggestedQuantity: z.boolean().optional(),
  hasAbnormalValues: z.boolean().optional(),
  hasShelfLifeWarnings: z.boolean().optional(),
  isKVI: z.boolean().optional(),
  storageTemperature: z.array(z.custom<StorageTemperatureType>()).optional(),

  // those are search params needed for "go to previous page" and "next recommendation" button
  vendorCodes: z.custom<string[]>().optional(),
  venueCodes: z.custom<string[]>().optional(),
  countryCodes: z.custom<CountryCode[]>().optional(),
  earliestFrom: z.string().optional(),
  latestTo: z.string().optional(),
  recommendationStatuses: z.custom<PORecommendation['status'][]>().optional(),
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  q: z.coerce.string().optional(),
  sortBy: z.custom<PurchaseOrdersRequestParams['sortBy']>().optional(),
  sortingType: z.custom<PurchaseOrdersRequestParams['sortingType']>().optional(),
});
